import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useFormik } from "formik";
import { LngLat } from "mapbox-gl";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useLeadStore, usePipedriveStore, usePopupStore, useViewSettingsStore } from "../..";
import useClosable from "../../hooks/useClosable";
import useLeadCreate from "../../hooks/useLeadCreate";
import useLeadCreateValidationSchema from "../../hooks/useLeadCreateValidationSchema";
import useNavigateToLead from "../../hooks/useNavigateToLead";
import useProperty from "../../hooks/useProperty";
import { GeoCoordinate } from "../../interfaces/GeoCoordinate";
import { ILeadCreateState, LeadCreateState, LeadOwnershipType, LeadStatus } from "../../interfaces/ILead";
import { ClosableOrder } from "../../store/ClosableStore";
import { leadOwnershipTypeToString } from "../../utils/enumUtils";
import commonStyles from "../Common.module.css";
import { CreateControls } from "../EditControls";
import ExpandIcon from "../icons/ExpandIcon";
import LeadIcon from "../icons/LeadIcon";
import { AddressInputRow } from "../inputs/AddressInput";
import { NumberInputRow } from "../inputs/NumberInput";
import { PhoneNumberInputRow } from "../inputs/PhoneNumberInput";
import { PipedriveCompanyInputRow } from "../inputs/PipedriveCompanyInput";
import PipedriveLabelSelect from "../inputs/PipedriveLabelSelect";
import { SelectInputRow } from "../inputs/SelectInput";
import { StringInputRow } from "../inputs/StringInput";
import TextInput from "../inputs/TextInput";
import MapPopupWithFlyTo from '../MapPopupWithFlyTo';
import popupStyles from "../Popup.module.css";
import OnMapSpinner from "../spinners/OnMapSpinner";
import styles from "./LeadPopup.module.css";
import LeadStatusWithIcon from "./LeadStatusWithIcon";

function LeadCreatePopup() {
    const { longitude, latitude } = useParams();
    const navigate = useNavigate();
    const createLead = useLeadCreate();
    const navigateToLead = useNavigateToLead();
    const leadStore = useLeadStore();
    const popupStore = usePopupStore();
    const pipedriveStore = usePipedriveStore();
    const viewSettingsStore = useViewSettingsStore();
    const coordinates = useMemo(() => new LngLat(Number(longitude), Number(latitude)), [longitude, latitude]);
    const { property, isLoading, fetchProperty } = useProperty(coordinates);
    const validationSchema = useLeadCreateValidationSchema();
    const initialState: ILeadCreateState | null = useMemo(() => computed(() => property ? {
        ...LeadCreateState.empty(),
        status: LeadStatus.OpenLead,
        address: property.address,
        ownerName: property.ownerEntity,
        zoning: property.zoning,
        landSize: property.landSize,
        buildingSize: property.buildingSize,
        lotCoverage: property.buildingSize ? Number((property.buildingSize / (property.landSize * 43560) * 100).toFixed(1)) : null,
        totalAssessment: property.totalAssessment,
        ownerAddress: property.ownerAddress,
    } : null), [property]).get();

    const formik = useFormik<ILeadCreateState>({
        initialValues: LeadCreateState.empty(),
        validationSchema: toFormikValidationSchema(validationSchema),
        onSubmit: async (values) => {
            const createdLeadId = await createLead(values, new GeoCoordinate(Number(latitude), Number(longitude)), property!.parcelId);
            navigateToLead(createdLeadId, true);
        },
    });

    useEffect(() => {
        if (initialState)
            formik.resetForm({ values: initialState });
    }, [initialState]);

    const close = () => navigate("/");
    const cancel = () => navigate(-1);
    useClosable(ClosableOrder.Medium, cancel);
    
    useEffect(() => fetchProperty(), [coordinates]);
    useEffect(() => popupStore.close(), []);
    useEffect(() => {
        if (popupStore.type)
            close();
    }, [popupStore.type]);

    if (isLoading)
        return <OnMapSpinner />;

    if (!property)
        return null;

    return (
        <MapPopupWithFlyTo
            longitude={coordinates.lng}
            latitude={coordinates.lat}
            closeButton={true}
            closeOnClick={false}
            onClose={close}
            anchor="left"
        >
            <div className={popupStyles.header}>
                <LeadIcon color="white" />
                <span className={popupStyles.title}>Add New Lead</span>
            </div>
            <div className={styles.selects}>
                <Select value={formik.values.status} onChange={e => formik.setFieldValue("status", e.target.value)} className={styles.select}>
                    {Object.values(LeadStatus).map(status => (
                        <MenuItem key={status} value={status} className={styles.select}>
                            <LeadStatusWithIcon status={status} />
                        </MenuItem>
                    ))}
                </Select>
                <PipedriveLabelSelect formik={formik} name="labelId" className={styles.select} />
            </div>
            <div className={commonStyles.separator}></div>
            <div className={[popupStyles.body, !viewSettingsStore.isPopupExpanded && styles.scrollable].join(" ")}>
                <AddressInputRow formik={formik} label="ADDRESS" name="address" />
                <StringInputRow formik={formik} label="OWNER NAME" name="ownerName" />
                <SelectInputRow formik={formik} label="OWNERSHIP TYPE" name="ownershipType" valueAsCorrectType={value => value as LeadOwnershipType}>
                    <option value="">----</option>
                    {Object.values(LeadOwnershipType).map(ownershipType => (
                        <option key={ownershipType} value={ownershipType}>{leadOwnershipTypeToString(ownershipType)}</option>
                    ))}
                </SelectInputRow>
                <StringInputRow formik={formik} label="ZONING" name="zoning" />
                <NumberInputRow formik={formik} label="LAND SIZE (ACRES)" name="landSize" />
                <NumberInputRow formik={formik} label="BUILDING SIZE (SQ-FT)" name="buildingSize" />
                <NumberInputRow formik={formik} label="LOT COVERAGE (%)" name="lotCoverage" />
                <NumberInputRow formik={formik} label="ASSESSMENT ($)" name="totalAssessment" />
                <AddressInputRow formik={formik} label="OWNER ADDRESS" name="ownerAddress" />
                <PipedriveCompanyInputRow formik={formik} label="COMPANY" name="pipedriveCompanyName" />
                <StringInputRow formik={formik} label="CONTACT NAME" name="contactName" />
                <StringInputRow formik={formik} label="EMAIL" name="email" />
                <PhoneNumberInputRow formik={formik} label="PHONE #" name="phoneNumber" />
                <TextInput formik={formik} label="" name="note" placeholder="Enter notes..." />
            </div>
            <div className={commonStyles.separator}></div>
            <div className={[commonStyles.controls, styles.controls].join(" ")}>
                <div className={commonStyles.controlGroup}>
                    <CreateControls
                        isStateChanged={formik.dirty}
                        isCreateLoading={leadStore.isLeadsLoading.creating! || pipedriveStore.isOrganizationsLoading.creating!}
                        onCreate={formik.handleSubmit}
                        onCancel={cancel}
                    />
                </div>
                <div className={commonStyles.controlGroup}>
                    <ExpandIcon />
                </div>
            </div>
        </MapPopupWithFlyTo>
    );
}

export default observer(LeadCreatePopup);
